import { useFetchAccountDetails } from '@/containers/Accounts/api';
import {
  selectAllAccounts,
  selectCurrentAccountId,
  selectRootAccounts,
} from '@/lib/redux/app.selectors';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

export interface RootAccountInfo {
  rootAccountId: string | null;
  rootAccountName: string | null;
  rootAccountParentId: string | null;
  rootAccountParentName: string | null;
  isLoading: boolean;
  isRootAccount: boolean;
}

interface UseGetRootAccountInfoOptions {
  accountId?: string;
}

/**
 * Hook to compute the user's root account information for a given account.
 *
 * Logic:
 * 1. Takes an accountId (defaults to current account from Redux)
 * 2. Fetches account details to get parent_account_key
 * 3. Parses parent_account_key (e.g., "11521234.11522345.11523456.11524567")
 * 4. Finds which ancestor ID exists in user's rootAccounts array
 * 5. Looks up that root account in allAccounts to get its details
 * 6. Returns root account info including its parent details
 *
 * @param options.accountId - Optional account ID to check (defaults to current account)
 */
export const useGetRootAccountInfo = (
  options: UseGetRootAccountInfoOptions = {}
): RootAccountInfo => {
  const { accountId: providedAccountId } = options;
  const currentAccountId = useSelector(selectCurrentAccountId);
  const rootAccounts = useSelector(selectRootAccounts);
  const allAccounts = useSelector(selectAllAccounts);

  // Use provided accountId or fall back to current account
  const accountId = providedAccountId || currentAccountId;

  // Fetch account details for the target account
  const { data: accountDetailsResponse, isLoading } = useFetchAccountDetails(
    accountId ?? undefined,
    !!accountId
  );

  const rootAccountInfo = useMemo((): RootAccountInfo => {
    // Default return value
    const defaultInfo: RootAccountInfo = {
      rootAccountId: null,
      rootAccountName: null,
      rootAccountParentId: null,
      rootAccountParentName: null,
      isLoading,
      isRootAccount: false,
    };

    if (!accountId || !accountDetailsResponse?.data || !rootAccounts || !allAccounts) {
      return defaultInfo;
    }

    const accountDetails = accountDetailsResponse.data;
    const parentAccountKey = accountDetails.parent_account_key;

    // If no parent_account_key, this is a top-level account
    if (!parentAccountKey) {
      // Check if this account is in the user's root accounts
      const isRoot = rootAccounts.includes(accountId);
      if (isRoot) {
        const account = allAccounts.find((acc) => acc.account_id === accountId);
        return {
          rootAccountId: accountId,
          rootAccountName: account?.account_name || null,
          rootAccountParentId: accountDetails.parent_account_id || null,
          rootAccountParentName: null, // Will look up below
          isLoading,
          isRootAccount: true,
        };
      }
      return defaultInfo;
    }

    // Parse parent_account_key to get all ancestor IDs
    // Format: "11521234.11522345.11523456.11524567"
    // The last ID is the current account, earlier IDs are ancestors
    const ancestorIds = parentAccountKey.split('.');

    // Find which ancestor is in the user's root accounts
    // Start from the beginning (highest level) as that's more likely to be the root
    let userRootAccountId: string | null = null;
    for (const ancestorId of ancestorIds) {
      if (rootAccounts.includes(ancestorId)) {
        userRootAccountId = ancestorId;
        break;
      }
    }

    // If no ancestor is in rootAccounts, check if the current account itself is a root
    if (!userRootAccountId) {
      if (rootAccounts.includes(accountId)) {
        userRootAccountId = accountId;
      } else {
        return defaultInfo;
      }
    }

    // Look up the root account in allAccounts to get its details
    const rootAccount = allAccounts.find((acc) => acc.account_id === userRootAccountId);

    if (!rootAccount) {
      return {
        ...defaultInfo,
        rootAccountId: userRootAccountId,
      };
    }

    // Get the root account's parent information
    let rootAccountParentName: string | null = null;
    if (rootAccount.parent_account_id) {
      const parentAccount = allAccounts.find(
        (acc) => acc.account_id === rootAccount.parent_account_id
      );
      rootAccountParentName = parentAccount?.account_name || null;
    }

    return {
      rootAccountId: rootAccount.account_id,
      rootAccountName: rootAccount.account_name,
      rootAccountParentId: rootAccount.parent_account_id || null,
      rootAccountParentName,
      isLoading,
      isRootAccount: accountId === userRootAccountId,
    };
  }, [accountId, accountDetailsResponse, rootAccounts, allAccounts, isLoading]);

  return rootAccountInfo;
};
